import React, { useState, useEffect } from 'react';
import { getSystemAuditTrail, getAuditTrailStats } from '@/services/grc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { 
  FileSearch, User, Clock, Filter, RefreshCw, Download,
  Shield, FileText, Settings, AlertTriangle, CheckCircle2,
  XCircle, Eye, Edit, Trash2, Plus, LogIn, LogOut, Lock, Loader2, Bug
} from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import ErrorLogDetail, { SeverityBadge } from '@/components/audit/ErrorLogDetail';
import ErrorRetentionSettings from '@/components/audit/ErrorRetentionSettings';

const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [scope, setScope] = useState('tenant');
  const { currentTenant, isSuperAdmin, loading: tenantLoading } = useTenant();
  const tenantKey = `${currentTenant?.id || ''}:${scope}`;

  useEffect(() => {
    if (!tenantLoading) fetchLogs();
  }, [actionFilter, resourceFilter, severityFilter, page, tenantKey, tenantLoading]);

  useEffect(() => {
    if (!tenantLoading) fetchStats();
  }, [tenantKey, tenantLoading]);

  const moduleOptions = Object.keys(stats?.errors_by_module || {});

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (resourceFilter !== 'all') params.resource_type = resourceFilter;
      if (search) params.search = search;
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (scope === 'platform') params.scope = 'platform';

      const data = await getSystemAuditTrail(params);
      setLogs(data.logs || []);
      setTotalLogs(data.total || 0);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getAuditTrailStats(scope === 'platform' ? { scope: 'platform' } : {});
      setStats(data);
    } catch { /* stats are optional */ }
  };

  const handleSearch = () => fetchLogs();

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4 text-green-500" />,
      logout: <LogOut className="w-4 h-4 text-gray-500" />,
      create: <Plus className="w-4 h-4 text-blue-500" />,
      update: <Edit className="w-4 h-4 text-yellow-500" />,
      delete: <Trash2 className="w-4 h-4 text-red-500" />,
      view: <Eye className="w-4 h-4 text-gray-500" />,
      approve: <CheckCircle2 className="w-4 h-4 text-green-500" />,
      reject: <XCircle className="w-4 h-4 text-red-500" />,
      export: <Download className="w-4 h-4 text-purple-500" />,
      error: <Bug className="w-4 h-4 text-red-600" />,
    };
    return icons[action?.toLowerCase()] || <FileSearch className="w-4 h-4 text-gray-500" />;
  };

  const getActionBadge = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-gray-100 text-gray-700',
      create: 'bg-blue-100 text-blue-700',
      update: 'bg-yellow-100 text-yellow-700',
      delete: 'bg-red-100 text-red-700',
      view: 'bg-gray-100 text-gray-700',
      approve: 'bg-green-100 text-green-700',
      reject: 'bg-red-100 text-red-700',
      export: 'bg-purple-100 text-purple-700',
      error: 'bg-red-600 text-white',
    };
    return colors[action?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getResourceIcon = (resourceType) => {
    const icons = {
      control: <Shield className="w-4 h-4" />,
      document: <FileText className="w-4 h-4" />,
      evidence: <FileSearch className="w-4 h-4" />,
      framework: <FileText className="w-4 h-4" />,
      user: <User className="w-4 h-4" />,
      settings: <Settings className="w-4 h-4" />,
      risk: <AlertTriangle className="w-4 h-4" />,
      audit: <FileSearch className="w-4 h-4" />,
    };
    return icons[resourceType?.toLowerCase()] || <FileText className="w-4 h-4" />;
  };

  const handleExport = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'IP Address', 'Severity', 'Status', 'Details'].join(','),
      ...logs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.user_email,
        log.action,
        log.resource_type,
        log.resource_id,
        log.ip_address || '',
        log.severity || '',
        log.status_code || '',
        typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || '')
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Audit trail exported');
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            System Audit Trail
            <Badge className="bg-emerald-500/10 text-emerald-700 text-[10px]"><Lock className="w-3 h-3 mr-1" /> Immutable</Badge>
          </h1>
          <p className="text-gray-500 mt-1">{scope === 'platform' ? 'Platform-level errors not tied to any tenant (unauthenticated requests)' : 'Immutable log of all system activities, changes and errors within your tenant'}</p>
          {scope !== 'platform' && !tenantLoading && <div className="mt-3"><ErrorRetentionSettings tenantKey={tenantKey} /></div>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }} className="gap-2" data-testid="refresh-audit-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={handleExport} className="gap-2" data-testid="export-audit-btn">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/10 rounded-lg">
                <FileSearch className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold" data-testid="audit-total-events">{stats?.total ?? totalLogs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-500/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today</p>
                <p className="text-2xl font-bold">{stats?.today || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-500/10 rounded-lg">
                <Edit className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Changes</p>
                <p className="text-2xl font-bold">{logs.filter(l => ['create', 'update', 'delete'].includes(l.action?.toLowerCase())).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unique Users</p>
                <p className="text-2xl font-bold">{new Set(logs.map(l => l.user_email)).size}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-red-300 transition-colors" onClick={() => { setActionFilter('error'); setPage(1); }}
          data-testid="errors-stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Errors</p>
                <p className="text-2xl font-bold" data-testid="errors-total">{stats?.errors_total || 0}</p>
                <p className="text-xs text-gray-500" data-testid="errors-today">{stats?.errors_today || 0} today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search logs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            {isSuperAdmin && (
              <Select value={scope} onValueChange={(v) => { setScope(v); setPage(1); }}>
                <SelectTrigger className="w-[190px]" data-testid="audit-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">Current tenant</SelectItem>
                  <SelectItem value="platform">Platform (no tenant)</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px]" data-testid="audit-action-select">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="logout">Logout</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="export">Export</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            {actionFilter === 'error' && (
              <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[160px]" data-testid="audit-severity-select">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All severities</SelectItem>
                  <SelectItem value="critical">Critical (5xx)</SelectItem>
                  <SelectItem value="security">Security (401/403)</SelectItem>
                  <SelectItem value="warning">Warning (4xx)</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={resourceFilter} onValueChange={(v) => { setResourceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[170px]" data-testid="audit-resource-select">
                <SelectValue placeholder="Resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="policy">Policy</SelectItem>
                <SelectItem value="policy_pack">Policy Pack</SelectItem>
                <SelectItem value="control">Control</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="evidence">Evidence</SelectItem>
                <SelectItem value="framework">Framework</SelectItem>
                <SelectItem value="risk">Risk</SelectItem>
                <SelectItem value="audit">Audit</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="consent">Consent</SelectItem>
                <SelectItem value="dsr">DSR</SelectItem>
                <SelectItem value="breach">Breach</SelectItem>
                {moduleOptions.map((m) => (
                  <SelectItem key={`mod-${m}`} value={m}>Module: {m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleSearch} data-testid="search-audit-btn">Search</Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="w-5 h-5" />
            Activity Log ({logs.length} events)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
              <p className="mt-4 text-gray-500">Loading audit logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileSearch className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">No audit logs found matching your filters</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div 
                  key={log.id || index} 
                  className="p-4 border rounded-lg overflow-hidden hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" 
                  data-testid={`audit-log-${log.id || index}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                    {/* Action Icon */}
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg w-fit">
                      {getActionIcon(log.action)}
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionBadge(log.action)}`}>
                          {log.action?.toUpperCase()}
                        </span>
                        {log.action === 'error' && <SeverityBadge severity={log.severity} status={log.status_code} />}
                        {log.resource_type && (
                          <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                            {getResourceIcon(log.resource_type)}
                            {log.resource_type}
                          </span>
                        )}
                        {log.resource_id && (
                          <span className="text-sm font-mono text-gray-500 break-all">
                            ID: {log.resource_id}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <User className="w-3 h-3" />
                        <span className="font-medium">{log.user_email || 'System'}</span>
                        {log.ip_address && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span>IP: {log.ip_address}</span>
                          </>
                        )}
                      </div>
                      
                      {log.details && (
                        <p className="mt-1 text-sm text-gray-500 break-words">
                          {typeof log.details === 'object' 
                            ? JSON.stringify(log.details, null, 2)
                            : log.details}
                        </p>
                      )}
                      {log.action === 'error' && <ErrorLogDetail log={log} />}
                    </div>
                    
                    {/* Timestamp */}
                    <div className="sm:text-right shrink-0 order-first sm:order-none">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {totalLogs > 50 && (
                <div className="flex items-center justify-between pt-3" data-testid="audit-pagination">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}
                    data-testid="audit-prev-page-btn">Previous</Button>
                  <span className="text-xs text-gray-500" data-testid="audit-page-indicator">
                    Page {page} of {Math.max(1, Math.ceil(totalLogs / 50))}
                  </span>
                  <Button variant="outline" size="sm" disabled={page >= Math.ceil(totalLogs / 50)}
                    onClick={() => setPage(page + 1)} data-testid="audit-next-page-btn">Next</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditTrailPage;
